import React, { useEffect, useMemo, useState } from "react";
import type { FindingRecord } from "../api/client";
import SeverityBadge from "./SeverityBadge";

export type SuppressPayload = {
  reason: string;
  expires_at?: string;
};

type Props = {
  open: boolean;
  finding: FindingRecord | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: SuppressPayload) => void;
};

const REASONS = ["False Positive", "Accepted Risk", "Won't Fix", "Other"] as const;

export default function SuppressModal({ open, finding, loading, error, onClose, onSubmit }: Props) {
  const [reason, setReason] = useState<(typeof REASONS)[number]>("False Positive");
  const [otherReason, setOtherReason] = useState("");
  const [expiresAt, setExpiresAt] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    setReason("False Positive");
    setOtherReason("");
    setExpiresAt("");
  }, [open]);

  const finalReason = useMemo(() => {
    if (reason !== "Other") return reason;
    const x = otherReason.trim();
    return x ? `Other: ${x}` : "Other";
  }, [reason, otherReason]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => { if (!loading) onClose(); }}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg rounded-xl border border-border bg-bg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 bg-surface1">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-text">Suppress Finding</h2>
              <p className="text-sm text-muted mt-0.5">
                Hidden from PR guardrails, visible in audit logs
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-1.5 rounded-lg text-muted hover:text-text hover:bg-surface2 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5 bg-bg">
          {/* Finding Preview */}
          {finding && (
            <div className="p-4 rounded-xl bg-surface1 border border-border">
              <div className="flex items-start gap-3">
                <SeverityBadge severity={finding.severity} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-text truncate">{finding.title}</div>
                  <div className="mt-1 text-xs text-muted flex items-center gap-2">
                    {finding.file_path && (
                      <span className="font-mono text-text2 truncate max-w-xs">{finding.file_path}</span>
                    )}
                    <span className="text-muted">•</span>
                    <span>Paxley Engine</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-text mb-2">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value as any)}
                className="w-full rounded-lg border border-border bg-surface1 px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand/40"
              >
                {REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Expiration <span className="text-muted font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface1 px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
          </div>

          {reason === "Other" && (
            <div>
              <label className="block text-sm font-medium text-text mb-2">Details</label>
              <input
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Describe why this is being suppressed..."
                className="w-full rounded-lg border border-border bg-surface1 px-3.5 py-2.5 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-4 rounded-xl bg-critical/10 border border-critical/20">
              <div className="flex items-center gap-2 text-sm text-critical">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4 flex justify-end gap-3 bg-surface1">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-surface1 text-text hover:bg-surface2 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSubmit({ reason: finalReason, expires_at: expiresAt || undefined })
            }
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-brand text-white hover:bg-brand-hover transition-colors disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Suppressing...
              </span>
            ) : (
              "Suppress Finding"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
